import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";
import { Lock, User, Eye, EyeOff, ArrowLeft } from "lucide-react";

// SHA-256 hash via the browser's native Web Crypto API. The password is never
// stored or compared in plaintext — only its hash touches this source file.
async function hashPassword(raw: string): Promise<string> {
  const enc = new TextEncoder().encode(raw);
  const digest = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// --- Why this is hardcoded instead of reading an `admin_users` table ---
// HappySpace's public data API does not allow public READ access on data
// tables for this business (confirmed directly against the live API: every
// table, not just this one, returns "Public read access is not allowed for
// this table" on GET, even though POST/insert succeeds fine). A login screen
// that depends on reading a table to know "does an account exist yet" can
// never work under that constraint — the read will always fail and come back
// empty, so the UI gets stuck showing "Create Admin Account" forever no
// matter how many accounts were ever inserted. That was the exact bug being
// reported: password + setup code accepted, account "created" (inserted
// fine), but the next visit reads back zero rows and shows setup again.
//
// Fix: a single admin credential lives here as a username + SHA-256 password
// hash, baked into source at build time. No network read is needed to log in,
// so the page can never revert to a setup screen. To change the password,
// hash a new value (e.g. in a browser devtools console:
//   crypto.subtle.digest('SHA-256', new TextEncoder().encode('newpassword'))
//     .then(b => console.log([...new Uint8Array(b)].map(x=>x.toString(16).padStart(2,'0')).join('')))
// then replace ADMIN_PASSWORD_HASH below with the printed hex string).
const ADMIN_USERNAME = "admin";
// Baked-in fallback hash — used only if no reset has ever been performed on
// this browser. Current password: DDS@Frisco2026#Abhi!
const ADMIN_PASSWORD_HASH =
  "1122961e105b4063ead09d48107714861427bce0471b69db9b8bbb475cd7921c";

// --- Self-service password reset ---
// Since reads on backend tables are unavailable (see note above), the reset
// flow stores a hash override in localStorage on THIS browser only. It is
// gated by a recovery code (also just a hash, never stored in plaintext).
// Recovery code: DDS-Reset-9417 — keep this safe, it's the only way to reset
// the password yourself without asking to have the source file changed.
const RECOVERY_CODE_HASH =
  "0f1271b346492de6f63ad945a8b20693f6041807b0d528ad1dcf7684fe2b402d";
const PASSWORD_OVERRIDE_KEY = "dds_admin_password_hash_override";

function getActiveHash(): string {
  return localStorage.getItem(PASSWORD_OVERRIDE_KEY) || ADMIN_PASSWORD_HASH;
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAdmin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset-password panel state
  const [showReset, setShowReset] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const cleanUsername = username.trim();
    const enteredHash = await hashPassword(password);

    if (
      cleanUsername.toLowerCase() === ADMIN_USERNAME.toLowerCase() &&
      enteredHash === getActiveHash()
    ) {
      login(cleanUsername, "super_admin");
      navigate("/");
    } else {
      setError("Invalid username or password. Please try again.");
    }

    setLoading(false);
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError("");
    setResetMessage("");
    setResetLoading(true);

    const enteredCodeHash = await hashPassword(recoveryCode.trim());

    if (enteredCodeHash !== RECOVERY_CODE_HASH) {
      setResetError("Invalid recovery code.");
      setResetLoading(false);
      return;
    }
    if (newPassword.length < 8) {
      setResetError("New password must be at least 8 characters.");
      setResetLoading(false);
      return;
    }

    const newHash = await hashPassword(newPassword);
    localStorage.setItem(PASSWORD_OVERRIDE_KEY, newHash);
    setResetMessage(
      "Password updated on this browser. It will work here immediately. Note: this override only applies on this device/browser — to make it permanent everywhere, share the new password so it can be baked into the source."
    );
    setNewPassword("");
    setRecoveryCode("");
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#f59e0b]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#082f6d]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Back to site */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-gray-500 hover:text-[#082f6d] text-sm mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to site
        </button>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          {/* Logo / Brand */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#082f6d] mb-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>
              Login
            </h1>
          </div>

          {!showReset ? (
            <>
              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Username"
                      required
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d] transition-all"
                      style={{ fontFamily: "'Lora', serif" }}
                    />
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      required
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d] transition-all"
                      style={{ fontFamily: "'Lora', serif" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-[#082f6d] hover:bg-[#061f4a] text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  style={{ fontFamily: "'Lora', serif" }}
                >
                  {loading ? "Please wait..." : "Sign In"}
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setShowReset(true);
                  setError("");
                }}
                className="w-full text-center text-xs text-gray-400 hover:text-[#082f6d] mt-5 transition-colors"
                style={{ fontFamily: "'Lora', serif" }}
              >
                Forgot password? Reset with recovery code
              </button>
            </>
          ) : (
            <>
              <p
                className="text-sm text-gray-500 mb-5 text-center"
                style={{ fontFamily: "'Lora', serif" }}
              >
                Enter your recovery code to set a new password on this device.
              </p>

              {resetError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {resetError}
                </div>
              )}
              {resetMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  {resetMessage}
                </div>
              )}

              <form onSubmit={handleReset} className="space-y-5">
                <div>
                  <input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    placeholder="Recovery code"
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d] transition-all"
                    style={{ fontFamily: "'Lora', serif" }}
                  />
                </div>

                <div>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 8 characters)"
                    required
                    minLength={8}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082f6d]/20 focus:border-[#082f6d] transition-all"
                    style={{ fontFamily: "'Lora', serif" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full py-2.5 bg-[#f59e0b] hover:bg-[#d97f06] text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  style={{ fontFamily: "'Lora', serif" }}
                >
                  {resetLoading ? "Please wait..." : "Set New Password"}
                </button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setShowReset(false);
                  setResetError("");
                  setResetMessage("");
                }}
                className="w-full text-center text-xs text-gray-400 hover:text-[#082f6d] mt-5 transition-colors"
                style={{ fontFamily: "'Lora', serif" }}
              >
                Back to login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
